import type { Dispatch, SetStateAction } from 'react';
import type { Helix2000Character } from '../../../shared/CharacterTypes';
import { getAbilityData } from '../../../shared/content/AbilityDataAdapter';
import { abilityChargeConfig } from '../../../shared/combat/abilityCharges';
import { projectTalentTree } from '../../../shared/progression/talents';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { chatHandler } from '../../ui/contexts/ChatContext';
import { CooldownStore } from '../../systems/player/CooldownStore';
import type {
  AbilityTrainingAvailableMessage, LearnedAbilitiesUpdateMessage, LearnAbilityResultMessage,
  TalentStateUpdateMessage, SpendTalentResultMessage, ProgressionCostOffersMessage,
  ProgressionRespecResultMessage, RespecStatsResultMessage,
} from '../../network/ClientMessageTypes';

const TRAINER_REJECTION_TEXT: Record<NonNullable<LearnAbilityResultMessage['reason']>, string> = {
  player_not_found: 'Your character is not available.',
  npc_not_found: 'That trainer is no longer here.',
  too_far: 'Move closer to the trainer.',
  not_a_trainer: 'That NPC cannot train abilities.',
  wrong_class: 'This trainer does not teach your class.',
  ability_not_eligible: 'That ability is not available at your current level.',
  already_learned: 'You already know that ability.',
  not_enough_gold: 'You do not have enough gold for that training.',
  persistence_failed: 'Training could not be saved. Your gold was not charged.',
  invalid_data: 'The training request was invalid.',
  error: 'Training failed. Please try again.',
};

function prettyAbilityName(abilityId: string | undefined): string {
  return getAbilityData(abilityId ?? '')?.name ?? (abilityId ?? 'ability').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function abilityTrainingAvailableText(message: AbilityTrainingAvailableMessage): string | null {
  if (!Array.isArray(message.abilityIds) || message.abilityIds.length === 0) return null;
  const pretty = message.abilityIds.map(prettyAbilityName).join(', ');
  return `Ability available to train: ${pretty}. Visit your class trainer.`;
}

export function trainerResultText(message: LearnAbilityResultMessage): string {
  if (message.ok === true) return `Learned ${prettyAbilityName(message.abilityId)}.`;
  return TRAINER_REJECTION_TEXT[message.reason ?? 'error'];
}

const TALENT_REJECTION_TEXT: Record<NonNullable<SpendTalentResultMessage['reason']>, string> = {
  player_not_found: 'Your character is not available.',
  tree_not_found: 'That talent tree is not available for your class.',
  node_not_found: 'That talent is not available for your class.',
  prerequisite_locked: 'Unlock the prerequisite talent first.',
  max_rank: 'That talent is already at maximum rank.',
  not_enough_points: 'You do not have enough talent points.',
  invalid_data: 'The talent request was invalid.',
  error: 'Talent allocation failed. Please try again.',
};

function prettyTalentName(nodeId: string | undefined): string {
  return (nodeId ?? 'talent').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function talentResultText(message: SpendTalentResultMessage): string {
  if (message.ok === true) {
    return `Allocated ${prettyTalentName(message.nodeId)} rank ${message.rank ?? '?'}.`;
  }
  return TALENT_REJECTION_TEXT[message.reason ?? 'error'];
}

function isTalentStateUpdate(message: TalentStateUpdateMessage | null | undefined): message is Required<TalentStateUpdateMessage> {
  if (!message || !message.ranks || typeof message.ranks !== 'object') return false;
  const points = [message.earnedPoints, message.spentPoints, message.availablePoints];
  if (!points.every(value => Number.isSafeInteger(value) && (value ?? -1) >= 0)) return false;
  if (message.earnedPoints !== message.spentPoints! + message.availablePoints!) return false;
  return Object.entries(message.ranks).every(([nodeId, rank]) => (
    /^[a-z][a-z0-9_]*$/.test(nodeId)
    && Number.isSafeInteger(rank)
    && rank > 0
  ));
}

function isProgressionCostOffers(
  message: ProgressionCostOffersMessage | null | undefined,
): message is Required<ProgressionCostOffersMessage> {
  if (!message?.talentRespec || !message.statRespec) return false;
  const offers = [message.talentRespec, message.statRespec];
  return offers.every(offer => (
    typeof offer.available === 'boolean'
    && typeof offer.quote?.policyId === 'string'
    && Number.isSafeInteger(offer.quote?.depth)
    && (offer.quote?.depth ?? -1) >= 0
    && Number.isSafeInteger(offer.quote?.cost)
    && (offer.quote?.cost ?? -1) >= 0
  ));
}

function respecResultText(
  label: 'Talents' | 'Stats',
  message: ProgressionRespecResultMessage | RespecStatsResultMessage,
): string {
  if (message.ok === true) return `${label} reset for ${message.goldSpent ?? 0} Gold.`;
  switch (message.reason) {
  case 'nothing_to_respec': return `No ${label.toLowerCase()} are allocated to reset.`;
  case 'not_enough_gold': return `You need ${message.quote?.cost ?? '?'} Gold to reset ${label.toLowerCase()}.`;
  case 'persistence_failed': return `${label} reset could not be saved. Your gold was not charged.`;
  case 'invalid_data': return `The ${label.toLowerCase()} reset request was invalid.`;
  case 'player_not_found': return 'Your character is not available.';
  default: return `${label} reset failed. Please try again.`;
  }
}

/** Subscribe once per connection-hook mount; notices do not own resource resets. */
export function registerProgressionEvents(setPlayer: Dispatch<SetStateAction<Helix2000Character | null>>): () => void {
  const unsubAbilityTrainingAvailable = EventBus.getInstance().on(
    EVENTS.NET_MESSAGE_ABILITY_TRAINING_AVAILABLE,
    (message: AbilityTrainingAvailableMessage) => {
      const text = abilityTrainingAvailableText(message);
      if (!text) return;
      chatHandler.addMessage({ id: `${Date.now()}_train`, sender: 'System', text, timestamp: Date.now(), type: 'system' });
      try { (globalThis as any).__errorToast?.('info', text); } catch { /* toast optional */ }
    },
  );

  const unsubLearnedAbilitiesUpdate = EventBus.getInstance().on(
    EVENTS.NET_MESSAGE_LEARNED_ABILITIES_UPDATE,
    (message: LearnedAbilitiesUpdateMessage) => {
      if (!message || !Array.isArray(message.abilities)
        || !message.abilities.every(id => typeof id === 'string')) return;
      const abilities = [...new Set(message.abilities)];
      for (const id of abilities) {
        if (abilityChargeConfig(id)) CooldownStore.initCharges(id);
      }
      setPlayer(prev => prev ? { ...prev, learnedAbilities: abilities } : null);
    },
  );

  const unsubLearnAbilityResult = EventBus.getInstance().on(
    EVENTS.NET_MESSAGE_LEARN_ABILITY_RESULT,
    (message: LearnAbilityResultMessage) => {
      if (!message) return;
      const text = trainerResultText(message);
      chatHandler.addMessage({
        id: `${Date.now()}_trainer`,
        sender: 'Trainer',
        text,
        timestamp: Date.now(),
        type: 'system',
      });
      try {
        (globalThis as any).__errorToast?.(message.ok === true ? 'success' : 'error', text);
      } catch { /* toast optional */ }
    },
  );

  const unsubTalentStateUpdate = EventBus.getInstance().on(
    EVENTS.NET_MESSAGE_TALENT_STATE_UPDATE,
    (message: TalentStateUpdateMessage) => {
      if (!isTalentStateUpdate(message)) return;
      setPlayer(prev => prev ? {
        ...prev,
        skillPoints: message.availablePoints,
        talents: projectTalentTree(prev.class, message.ranks),
      } : null);
    },
  );

  const unsubSpendTalentResult = EventBus.getInstance().on(
    EVENTS.NET_MESSAGE_SPEND_TALENT_RESULT,
    (message: SpendTalentResultMessage) => {
      if (!message) return;
      const text = talentResultText(message);
      chatHandler.addMessage({
        id: `${Date.now()}_talent`,
        sender: 'Talents',
        text,
        timestamp: Date.now(),
        type: 'system',
      });
      try {
        (globalThis as any).__errorToast?.(message.ok === true ? 'success' : 'error', text);
      } catch { /* toast optional */ }
    },
  );

  const unsubProgressionCostOffers = EventBus.getInstance().on(
    EVENTS.NET_MESSAGE_PROGRESSION_COST_OFFERS,
    (message: ProgressionCostOffersMessage) => {
      if (!isProgressionCostOffers(message)) return;
      setPlayer(prev => prev ? { ...prev, progressionCostOffers: message } : null);
    },
  );

  const showRespecResult = (
    label: 'Talents' | 'Stats',
    message: ProgressionRespecResultMessage | RespecStatsResultMessage,
  ) => {
    if (!message) return;
    const text = respecResultText(label, message);
    chatHandler.addMessage({
      id: `${Date.now()}_respec`,
      sender: label,
      text,
      timestamp: Date.now(),
      type: 'system',
    });
    try {
      (globalThis as any).__errorToast?.(message.ok === true ? 'success' : 'error', text);
    } catch { /* toast optional */ }
  };
  const unsubRespecTalentsResult = EventBus.getInstance().on(
    EVENTS.NET_MESSAGE_RESPEC_TALENTS_RESULT,
    (message: ProgressionRespecResultMessage) => showRespecResult('Talents', message),
  );
  const unsubRespecStatsResult = EventBus.getInstance().on(
    EVENTS.NET_MESSAGE_RESPEC_STATS_RESULT,
    (message: RespecStatsResultMessage) => showRespecResult('Stats', message),
  );

  return () => {
    unsubAbilityTrainingAvailable();
    unsubLearnedAbilitiesUpdate();
    unsubLearnAbilityResult();
    unsubTalentStateUpdate();
    unsubSpendTalentResult();
    unsubProgressionCostOffers();
    unsubRespecTalentsResult();
    unsubRespecStatsResult();
  };
}
